import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Sparkles, Film, Star, Menu, ShieldCheck } from "lucide-react";
import { CrownLogo } from "./CrownLogo";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const go = (path) => { setOpen(false); navigate(path); };

  const MobileLink = ({ to, icon: Icon, children, testid }) => (
    <button onClick={() => go(to)} className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-cream hover:bg-white/5 hover:text-gold" data-testid={testid}>
      <Icon className="h-5 w-5 text-gold" /> {children}
    </button>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-white/5 bg-black/60 backdrop-blur-2xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Link to={user ? "/dashboard" : "/"} data-testid="nav-home-link">
          <CrownLogo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex md:gap-2">
          <Button variant="ghost" className="rounded-full text-cream hover:text-gold" onClick={() => navigate("/gallery")} data-testid="nav-gallery-btn">
            <Film className="mr-2 h-4 w-4" /> Transformations
          </Button>
          <Button variant="ghost" className="rounded-full text-cream hover:text-gold" onClick={() => navigate("/reviews")} data-testid="nav-reviews-btn">
            <Star className="mr-2 h-4 w-4" /> Reviews
          </Button>
          {user ? (
            <>
              {user.role === "admin" && (
                <Button variant="ghost" className="rounded-full text-gold hover:text-cream" onClick={() => navigate("/admin")} data-testid="nav-admin-btn">
                  <ShieldCheck className="mr-2 h-4 w-4" /> Admin
                </Button>
              )}
              <Button variant="ghost" className="rounded-full text-cream hover:text-gold" onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-btn">
                <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
              </Button>
              <Button variant="ghost" className="rounded-full text-cream hover:text-gold" onClick={() => navigate("/products")} data-testid="nav-products-btn">
                <Sparkles className="mr-2 h-4 w-4" /> Products
              </Button>
              <Button variant="ghost" className="rounded-full text-muted-foreground hover:text-cream" onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button className="rounded-full bg-gold text-charcoal hover:bg-gold/90" onClick={() => navigate("/auth")} data-testid="nav-signin-btn">
              Sign in
            </Button>
          )}
        </nav>

        {/* Mobile nav */}
        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="flex h-10 w-10 items-center justify-center rounded-full border border-charcoal-border text-cream hover:text-gold" data-testid="mobile-menu-btn" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 border-charcoal-border bg-charcoal text-cream" data-testid="mobile-menu-panel">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="mb-8 mt-2"><CrownLogo /></div>
              <div className="flex flex-col gap-1">
                <MobileLink to="/gallery" icon={Film} testid="mobile-nav-gallery">Transformations</MobileLink>
                <MobileLink to="/reviews" icon={Star} testid="mobile-nav-reviews">Reviews</MobileLink>
                {user ? (
                  <>
                    {user.role === "admin" && (
                      <MobileLink to="/admin" icon={ShieldCheck} testid="mobile-nav-admin">Admin Console</MobileLink>
                    )}
                    <MobileLink to="/dashboard" icon={LayoutDashboard} testid="mobile-nav-dashboard">Dashboard</MobileLink>
                    <MobileLink to="/products" icon={Sparkles} testid="mobile-nav-products">Products</MobileLink>
                    <button onClick={() => { logout(); go("/"); }} className="mt-4 flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-muted-foreground hover:bg-white/5 hover:text-cream" data-testid="mobile-nav-logout">
                      <LogOut className="h-5 w-5" /> Sign out
                    </button>
                  </>
                ) : (
                  <Button className="mt-4 w-full rounded-full bg-gold text-charcoal hover:bg-gold/90" onClick={() => go("/auth")} data-testid="mobile-nav-signin">
                    Sign in
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
